// import React from 'react';
import { motion } from 'framer-motion';
import Navbar from './navbar';
import Footer from './Footer';
import { FiAward, FiBarChart, FiDatabase, FiGlobe, FiTrendingUp, FiUsers, FiInfo, FiPackage } from 'react-icons/fi';

const SuccessStories = () => {
  const featuredStories = [
    {
      sector: "Healthcare",
      company: "Huma Platform",
      metrics: ["30% reduction in readmissions", "40% less review time"],
      description: "AI-powered digital health platform revolutionized patient care, improving efficiency and outcomes.",
      impact: "Transformed patient monitoring and care delivery across multiple healthcare institutions.",
      icon: <FiUsers className="w-8 h-8" />
    },
    {
      sector: "Finance",
      company: "PayPal",
      metrics: ["99.9% fraud detection rate", "$2.2B saved annually"],
      description: "Advanced AI fraud detection analyzes millions of transactions in real-time.",
      impact: "Protected millions of users while maintaining seamless transaction processing.",
      icon: <FiBarChart className="w-8 h-8" />
    },
    {
      sector: "Manufacturing",
      company: "Smart Factory Solutions",
      metrics: ["85% defect reduction", "60% efficiency increase"],
      description: "AI-powered quality control and predictive maintenance systems revolutionized production.",
      impact: "Revolutionized production processes and quality assurance.",
      icon: <FiDatabase className="w-8 h-8" />
    }
  ];

  const testimonials = [
    {
      quote: "The AI use case discovery platform helped us identify and implement solutions that transformed our operations.",
      author: "CTO, Global Manufacturing Corp",
      impact: "45% operational efficiency increase"
    },
    {
      quote: "We found exactly the AI solutions we needed for our healthcare practice through this platform.",
      author: "Director of Innovation, Healthcare Solutions",
      impact: "2x patient satisfaction improvement"
    },
    {
      quote: "The sector-specific approach made it easy to find relevant AI implementations for our industry.",
      author: "Head of Digital Transformation, Financial Services",
      impact: "3x ROI on AI investments"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-gray-950 to-black font-sans">
      <Navbar />

      {/* Hero Section */}
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-extrabold mb-4 text-white tracking-tight">
              Success Stories
            </h1>
            <span className="block text-xl text-orange-400 font-semibold mb-6">Real Impact, Real Results</span>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-2">
              Discover how organizations across sectors leverage AI to transform operations and achieve breakthrough results.
            </p>
          </motion.div>

          {/* Featured Success Stories — Mission Pillars style cards */}
          <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative mt-16 text-left">
            <h3
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '150%',
                color: 'rgba(255, 255, 255, 1)',
              }}
              className="mb-6"
            >
              Featured Success Stories
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredStories.map((story, index) => (
                <motion.div
                  key={story.company + index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex flex-col justify-start px-4 py-6 bg-black/50 rounded-lg border border-white/10 h-full"
                >
                  <div className="text-orange-400 mb-3">{story.icon}</div>
                  <h4
                    style={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 700,
                      fontSize: '22px',
                      lineHeight: '150%',
                      color: 'rgba(255,255,255,1)',
                    }}
                    className="mb-1"
                  >
                    {story.sector}
                  </h4>
                  <div
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '140%',
                      color: 'rgba(255,84,31,1)',
                      letterSpacing: '0.02em',
                    }}
                    className="uppercase tracking-wide mb-3"
                  >
                    {story.company}
                  </div>
                  <p
                    style={{
                      fontFamily: 'Lora, serif',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '160%',
                      color: 'rgba(200,200,200,1)',
                    }}
                    className="mb-3"
                  >
                    {story.description}
                  </p>

                  <ul className="mb-3 space-y-1">
                    {story.metrics.map((m, idx) => (
                      <li
                        key={idx}
                        style={{
                          fontFamily: 'Lora, serif',
                          fontWeight: 400,
                          fontSize: '15px',
                          lineHeight: '150%',
                          color: 'rgba(255, 255, 255, 0.85)',
                        }}
                        className="pl-3 relative"
                      >
                        <span className="absolute left-0 top-2 w-1 h-1 rounded-full bg-white/60" />
                        {m}
                      </li>
                    ))}
                  </ul>

                  <p
                    style={{
                      fontFamily: 'Lora, serif',
                      fontWeight: 400,
                      fontSize: '15px',
                      lineHeight: '150%',
                      color: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {story.impact}
                  </p>
                </motion.div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Testimonials Section — Mission Pillars style cards */}
      <div className="bg-gradient-to-b from-gray-900 to-black py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-4xl font-bold text-center mb-12 text-white"
          >
            What Our Users Say
          </motion.h2>

          <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
            <h3
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '150%',
                color: 'rgba(255, 255, 255, 1)',
              }}
              className="mb-6 text-left max-w-6xl mx-auto"
            >
              What Our Users Say
            </h3>

            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t, index) => (
                <motion.div
                  key={t.author + index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex flex-col justify-start px-4 py-6 bg-black/50 rounded-lg border border-white/10 h-full"
                >
                  <div className="text-orange-400 mb-3">
                    <FiAward className="w-6 h-6" />
                  </div>
                  <p
                    style={{
                      fontFamily: 'Lora, serif',
                      fontWeight: 500,
                      fontSize: '18px',
                      lineHeight: '160%',
                      color: 'rgba(255,255,255,0.92)',
                      fontStyle: 'italic',
                    }}
                    className="mb-4"
                  >
                    “{t.quote}”
                  </p>
                  <div
                    style={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '150%',
                      color: 'rgba(255,84,31,1)',
                      letterSpacing: '0.02em',
                    }}
                    className="uppercase tracking-wide mb-1"
                  >
                    {t.author}
                  </div>
                  {t.impact && (
                    <div
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                        fontSize: '13px',
                        lineHeight: '150%',
                        color: 'rgba(255,255,255,0.7)',
                      }}
                    >
                      {t.impact}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Global Impact Section */}
      <div className="bg-gradient-to-b from-black to-gray-900 py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-8 text-white">Our Global Impact</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <FiDatabase className="mx-auto mb-2 text-orange-400" size={32} />
                <div className="text-4xl font-bold text-orange-400 mb-2">500+</div>
                <div className="text-gray-300">Use Cases</div>
              </div>
              <div className="text-center">
                <FiGlobe className="mx-auto mb-2 text-orange-400" size={32} />
                <div className="text-4xl font-bold text-orange-400 mb-2">50+</div>
                <div className="text-gray-300">Industries</div>
              </div>
              <div className="text-center">
                <FiUsers className="mx-auto mb-2 text-orange-400" size={32} />
                <div className="text-4xl font-bold text-orange-400 mb-2">1000+</div>
                <div className="text-gray-300">Organizations</div>
              </div>
              <div className="text-center">
                <FiTrendingUp className="mx-auto mb-2 text-orange-400" size={32} />
                <div className="text-4xl font-bold text-orange-400 mb-2">95%</div>
                <div className="text-gray-300">Success Rate</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Impact Statistics Section */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
        <h3
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '150%',
            color: 'rgba(255, 255, 255, 1)',
          }}
          className="mb-3"
        >
          Our Impact
        </h3>
        <p
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '150%',
            color: 'rgba(140,140,140,1)',
          }}
          className="max-w-3xl"
        >
          Through our platform, we&apos;re helping organizations across industries harness the power of AI to drive
          innovation and achieve their strategic goals.
        </p>

        {/* Metrics Container */}
        <div className="mt-6 rounded-xl border border-white/10 bg-black/30 px-4 py-5 md:px-8 md:py-6 grid grid-cols-2 md:grid-cols-4">
          {[
            { value: "500+", label: "Use Cases Documented" },
            { value: "50+", label: "Industry Sectors" },
            { value: "1000+", label: "Organizations Supported" },
            { value: "95%", label: "User Satisfaction" },
          ].map((metric, idx) => (
            <div key={idx} className="flex flex-col items-center justify-center">
              <div className="text-2xl md:text-4xl font-bold text-orange-400 mb-1">{metric.value}</div>
              <div className="text-gray-300 text-sm md:text-base text-center">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Decorative diagonal light / subtle spark accents */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
          <div className="absolute -top-10 right-[-20%] w-[160%] h-full rotate-[20deg] bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-10" />
          <div className="absolute -bottom-4 left-10 w-1 h-1 rounded-full bg-orange-500/80" />
          <div className="absolute bottom-6 left-[45%] w-1 h-1 rounded-full bg-orange-500/70" />
          <div className="absolute bottom-2 right-[22%] w-1 h-1 rounded-full bg-orange-500/60" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Add content-only export
export const SuccessStoriesContent = () => {
  const featuredStories = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      sector: "Healthcare",
      company: "Huma Platform",
      details: "AI-powered digital health platform revolutionized patient care, improving efficiency.",
      metrics: ["30% reduction in readmissions", "40% less review time"],
      impact: "Transformed patient monitoring and care delivery across multiple healthcare institutions.",
    },
    {
      icon: <FiInfo className="w-6 h-6" />,
      sector: "Comprehensive Coverage",
      company: "PayPal",
      details: "Advanced AI fraud detection analyzes millions of transactions in real-time.",
      metrics: ["99.9% fraud detection rate", "$2.2B saved annually"],
      impact: "Protected millions of users while maintaining seamless processing.",
    },
    {
      icon: <FiPackage className="w-6 h-6" />,
      sector: "Practical Implementation",
      company: "Smart Factory Solutions",
      details: "AI-powered quality control and predictive maintenance systems.",
      metrics: ["85% defect reduction", "60% efficiency increase"],
      impact: "Revolutionized production processes and quality assurance.",
    },
  ];

  const testimonials = [
    {
      quote:
        "The AI use case discovery platform helped us identify and implement solutions that transformed our operations.",
      author: "CTO, Global Manufacturing Corp",
      impact: "45% operational efficiency increase",
    },
    {
      quote:
        "We found exactly the AI solutions we needed for our healthcare practice through this platform.",
      author: "Director of Innovation, Healthcare Solutions",
      impact: "2x patient satisfaction improvement",
    },
    {
      quote:
        "The sector-specific approach made it easy to find relevant AI implementations for our industry.",
      author: "Head of Digital Transformation, Financial Services",
      impact: "3x ROI on AI investments",
    },
  ];

  return (
    <div className="space-y-10">
      {/* Header */}
      <header>
        <h2
          style={{
            fontFamily: "Sora, sans-serif",
            fontWeight: 600,
            fontSize: "32px",
            lineHeight: "150%",
            color: "rgba(255, 84, 31, 1)",
          }}
          className="mb-4"
        >
          Success Stories
        </h2>
        <div className="mb-6">
          <span
            style={{
              fontFamily: "Sora, sans-serif",
              fontWeight: 700,
              fontSize: "24px",
              lineHeight: "200%",
              color: "rgba(255,255,255,1)",
            }}
            className="block mb-2"
          >
            Real Impact, Real Results
          </span>
          <span
            style={{
              fontFamily: "Sora, sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "150%",
              color: "rgba(140, 140, 140, 1)",
            }}
            className="block"
          >
            Discover how organizations across sectors leverage AI to transform operations and achieve breakthrough results.
          </span>
        </div>
      </header>

      {/* Featured Success Stories — card style */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
        <h3
          style={{
            fontFamily: "Sora, sans-serif",
            fontWeight: 600,
            fontSize: "24px",
            lineHeight: "150%",
            color: "rgba(255, 255, 255, 1)",
          }}
          className="mb-6"
        >
          Featured Success Stories
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featuredStories.map((story) => (
            <div
              key={story.company}
              className="flex flex-col justify-start px-4 py-6 bg-black/50 rounded-lg border border-white/10"
            >
              <div className="text-orange-400 mb-3">{story.icon}</div>
              <h4
                style={{
                  fontFamily: "IBM Plex Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "22px",
                  lineHeight: "150%",
                  color: "rgba(255,255,255,1)",
                }}
                className="mb-1"
              >
                {story.sector}
              </h4>
              <div
                style={{
                  fontFamily: "Sora, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "140%",
                  color: "rgba(255,84,31,1)",
                  letterSpacing: "0.02em",
                }}
                className="uppercase tracking-wide mb-3"
              >
                {story.company}
              </div>
              <p
                style={{
                  fontFamily: "Lora, serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "160%",
                  color: "rgba(200,200,200,1)",
                }}
                className="mb-3"
              >
                {story.details}
              </p>
              <ul className="mb-3 space-y-1">
                {story.metrics.map((m) => (
                  <li
                    key={m}
                    style={{
                      fontFamily: "Lora, serif",
                      fontWeight: 400,
                      fontSize: "15px",
                      lineHeight: "150%",
                      color: "rgba(255, 255, 255, 0.85)",
                    }}
                    className="pl-3 relative"
                  >
                    <span className="absolute left-0 top-2 w-1 h-1 rounded-full bg-white/60" />
                    {m}
                  </li>
                ))}
              </ul>
              <p
                style={{
                  fontFamily: "Lora, serif",
                  fontWeight: 400,
                  fontSize: "15px",
                  lineHeight: "150%",
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                {story.impact}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials — card style */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
        <h3
          style={{
            fontFamily: "Sora, sans-serif",
            fontWeight: 600,
            fontSize: "24px",
            lineHeight: "150%",
            color: "rgba(255, 255, 255, 1)",
          }}
          className="mb-6"
        >
          What Our Users Say
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((t) => (
            <div
              key={t.author}
              className="flex flex-col justify-start px-4 py-6 bg-black/50 rounded-lg border border-white/10"
            >
              <div className="text-orange-400 mb-3">
                <FiAward className="w-6 h-6" />
              </div>
              <p
                style={{
                  fontFamily: "Lora, serif",
                  fontWeight: 500,
                  fontSize: "18px",
                  lineHeight: "160%",
                  color: "rgba(255,255,255,0.92)",
                  fontStyle: "italic",
                }}
                className="mb-4"
              >
                “{t.quote}”
              </p>
              <div
                style={{
                  fontFamily: "IBM Plex Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "150%",
                  color: "rgba(255,84,31,1)",
                  letterSpacing: "0.02em",
                }}
                className="uppercase tracking-wide mb-1"
              >
                {t.author}
              </div>
              {t.impact && (
                <div
                  style={{
                    fontFamily: "Sora, sans-serif",
                    fontWeight: 600,
                    fontSize: "13px",
                    lineHeight: "150%",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  {t.impact}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Impact Statistics — keep as-is */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
        <h3
          style={{
            fontFamily: "Sora, sans-serif",
            fontWeight: 600,
            fontSize: "24px",
            lineHeight: "150%",
            color: "rgba(255, 255, 255, 1)",
          }}
          className="mb-3"
        >
          Our Global Impact
        </h3>
        <div className="mt-6 rounded-xl border border-white/10 bg-black/30 px-4 py-5 md:px-8 md:py-6 grid grid-cols-2 md:grid-cols-4">
          {[
            {
              value: "500+",
              label: "Use Cases Documented",
            },
            {
              value: "50+",
              label: "Industry Sectors",
            },
            {
              value: "1000+",
              label: "Organizations Supported",
            },
            {
              value: "95%",
              label: "User Satisfaction",
            },
          ].map((metric, idx) => (
            <div key={idx} className="flex flex-col items-center justify-center">
              <div className="text-2xl md:text-4xl font-bold text-orange-400 mb-1">
                {metric.value}
              </div>
              <div className="text-gray-300 text-sm md:text-base text-center">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
          <div className="absolute -top-10 right-[-20%] w-[160%] h-full rotate-[20deg] bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-10" />
          <div className="absolute -bottom-4 left-10 w-1 h-1 rounded-full bg-orange-500/80" />
          <div className="absolute bottom-6 left-[45%] w-1 h-1 rounded-full bg-orange-500/70" />
          <div className="absolute bottom-2 right-[22%] w-1 h-1 rounded-full bg-orange-500/60" />
        </div>
      </section>
    </div>
  );
};

export default SuccessStories;